import React, { useContext } from "react";
import { Counterparty } from "@common/types/counterparty";
import { Entity } from "@common/types/entity";
import { ImporterErrors } from "@common/types/financialDocument";
import { Button } from "@design-system/Button/Button";
import { DatePicker } from "@design-system/DatePicker/DatePicker";
import { Field, pickFieldControlAriaProps } from "@design-system/Field/Field";
import { Input } from "@design-system/Input/Input";
import { Tag } from "@design-system/Tag/Tag";
import { TextArea } from "@design-system/TextArea/TextArea";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { VStack } from "@design-system/VStack/VStack";
import { CheckIcon, CloseIcon } from "@icons";
import { SelectNativeInvoiceCustomer } from "@components/invoices/NativeInvoiceDrawer/copyNativeInvoicePartyDetails";
import { DetailsToFields } from "@components/invoices/NativeInvoiceDrawer/DetailsToFields";
import { FromEntitySelect } from "@components/invoices/NativeInvoiceDrawer/FromEntitySelect";
import { LineItemsSection } from "@components/invoices/NativeInvoiceDrawer/LineItemsSection";
import {
    getNativeInvoiceDueDateError,
    withDueDateOverride,
    withInvoiceDateOverride,
} from "@components/invoices/NativeInvoiceDrawer/nativeInvoiceFormHelpers";
import { NativeInvoiceLineRow } from "@components/invoices/NativeInvoiceDrawer/nativeInvoiceFormTypes";
import { useNativeInvoiceFormContext } from "@components/invoices/NativeInvoiceDrawer/NativeInvoiceFormContext";
import { resetCollapsedTextAreaScroll } from "@components/invoices/NativeInvoiceDrawer/resetCollapsedTextAreaScroll";
import styles from "@components/invoices/NativeInvoiceDrawer/SidePanel.module.scss";
import type { CounterpartyOcrPrefill } from "../buildCounterpartyPrefillFromPartyHints";
import { AccrualImporterContext } from "../accrualImporter.context";

interface Props {
    entities: Entity[];
    selectedEntity?: Entity;
    isEntityLocked: boolean;
    activeReviewTaskId?: string;
    onSelectEntity: (entity: Entity | undefined) => void;
    suggestedEntity?: Entity;
    onDismissSuggestedEntity?: () => void;
    onSelectCustomer: SelectNativeInvoiceCustomer;
    subtotal: number;
    total: number;
    onChangeLine: (
        lineId: string,
        patch: Partial<NativeInvoiceLineRow>,
    ) => void;
    onAddLine: () => void;
    onRemoveLine: (lineId: string) => void;
    partySubtitle: string;
    customerPlaceholder: string;
    suggestedCustomerName?: string | null;
    counterpartyCreatePrefill?: CounterpartyOcrPrefill | null;
    isCreateCustomerOpen?: boolean;
    onCreateCustomerOpenChange?: (isOpen: boolean) => void;
    onCustomerCreated: (counterparty: Counterparty) => void;
    senderEmail?: string | null;
    dateLabel: string;
    numberLabel: string;
    onNumberBlur: () => void;
    suggestedAccountLineIds?: readonly string[];
    onConfirmAccountSuggestion?: (lineId: string) => void;
    suggestedItemLineIds?: readonly string[];
    onConfirmItemSuggestion?: (lineId: string) => void;
    counterpartyDetailChanges?: React.ReactNode;
}

export const AccrualDraftDetailsSections: React.FC<Props> = ({
    entities,
    selectedEntity,
    isEntityLocked,
    activeReviewTaskId,
    onSelectEntity,
    suggestedEntity,
    onDismissSuggestedEntity,
    onSelectCustomer,
    subtotal,
    total,
    onChangeLine,
    onAddLine,
    onRemoveLine,
    partySubtitle,
    customerPlaceholder,
    suggestedCustomerName,
    counterpartyCreatePrefill,
    isCreateCustomerOpen,
    onCreateCustomerOpenChange,
    onCustomerCreated,
    senderEmail,
    dateLabel,
    numberLabel,
    onNumberBlur,
    suggestedAccountLineIds,
    onConfirmAccountSuggestion,
    suggestedItemLineIds,
    onConfirmItemSuggestion,
    counterpartyDetailChanges,
}) => {
    const { state, setState } = useNativeInvoiceFormContext();
    const { mode } = useContext(AccrualImporterContext);
    const dueDateError = getNativeInvoiceDueDateError(state);

    return (
        <VStack gap={16} paddingBlock={16} paddingInline={24}>
            <DetailsToFields
                onSelectCustomer={onSelectCustomer}
                partySubtitle={partySubtitle}
                customerPlaceholder={customerPlaceholder}
                suggestedCustomerName={suggestedCustomerName}
                counterpartyCreatePrefill={counterpartyCreatePrefill}
                isCreateCustomerOpen={isCreateCustomerOpen}
                onCreateCustomerOpenChange={onCreateCustomerOpenChange}
                counterpartyCreateFields="extracted"
                counterpartyCreateDirection="right"
                onCustomerCreated={onCustomerCreated}
                isCustomerReviewHighlighted={activeReviewTaskId === "customer"}
                customerAction={counterpartyDetailChanges}
            />
            <div className={styles.fieldGrid}>
                <FromEntitySelect
                    label="Entity"
                    entities={entities}
                    selectedEntity={selectedEntity}
                    onSelectEntity={onSelectEntity}
                    isEntityLocked={isEntityLocked}
                    isReviewHighlighted={activeReviewTaskId === "entity"}
                />
                <Field label={numberLabel}>
                    <Input
                        size="xl"
                        maxLength={255}
                        className={
                            activeReviewTaskId ===
                            ImporterErrors.ACCRUAL_POSSIBLE_DUPLICATE
                                ? styles.reviewTaskHighlight
                                : undefined
                        }
                        value={state.invoiceNumber}
                        onChange={(event) =>
                            setState((prev) => ({
                                ...prev,
                                invoiceNumber: event.target.value,
                            }))
                        }
                        onBlur={onNumberBlur}
                    />
                </Field>
                <Field label={dateLabel} control="button">
                    <Field.Control
                        render={(controlProps) => (
                            <DatePicker
                                mode="day"
                                variant="panel-input"
                                className={styles.datePicker}
                                clearable={false}
                                value={state.invoiceDate || undefined}
                                onValueChange={(value) =>
                                    setState((prev) =>
                                        withInvoiceDateOverride(
                                            prev,
                                            value ?? "",
                                        ),
                                    )
                                }
                                placeholder="Select"
                                buttonProps={{
                                    variant: "primary",
                                    size: "xl",
                                }}
                                fieldControlProps={pickFieldControlAriaProps(
                                    controlProps,
                                )}
                            />
                        )}
                    />
                </Field>
                <Field
                    label="Due date"
                    control="button"
                    invalid={dueDateError !== undefined}
                >
                    <Field.Control
                        render={(controlProps) => (
                            <DatePicker
                                mode="day"
                                variant="panel-input"
                                className={styles.datePicker}
                                clearable
                                minDate={state.invoiceDate || undefined}
                                value={state.dueDate || undefined}
                                onValueChange={(value) =>
                                    setState((prev) =>
                                        withDueDateOverride(prev, value ?? ""),
                                    )
                                }
                                placeholder="Select"
                                buttonProps={{
                                    variant: "primary",
                                    size: "xl",
                                    className:
                                        activeReviewTaskId === "dueDate"
                                            ? styles.reviewTaskHighlight
                                            : undefined,
                                }}
                                fieldControlProps={pickFieldControlAriaProps(
                                    controlProps,
                                )}
                            />
                        )}
                    />
                </Field>
                {senderEmail ? (
                    <Field label="Sender email">
                        <Input
                            size="xl"
                            type="email"
                            value={senderEmail}
                            readOnly
                        />
                    </Field>
                ) : null}
            </div>
            {suggestedEntity ? (
                <div className={styles.suggestedCustomerRow}>
                    <span className={styles.suggestedCustomerName}>
                        {suggestedEntity.name}
                    </span>
                    <Tag size="sm">Suggested</Tag>
                    <Tooltip content="Switch to suggested entity">
                        <Button
                            type="button"
                            variant="accent"
                            size="md"
                            iconOnly
                            disabled={isEntityLocked}
                            aria-label="Confirm suggested entity"
                            onClick={() => onSelectEntity(suggestedEntity)}
                        >
                            <CheckIcon />
                        </Button>
                    </Tooltip>
                    {onDismissSuggestedEntity ? (
                        <Tooltip content="Keep current entity">
                            <Button
                                type="button"
                                variant="secondary"
                                size="md"
                                iconOnly
                                aria-label="Dismiss suggested entity"
                                onClick={onDismissSuggestedEntity}
                            >
                                <CloseIcon />
                            </Button>
                        </Tooltip>
                    ) : null}
                </div>
            ) : null}
            <LineItemsSection
                lines={state.lines}
                entityId={selectedEntity?.id}
                subtotal={subtotal}
                total={total}
                onChangeLine={onChangeLine}
                onAddLine={onAddLine}
                onRemoveLine={onRemoveLine}
                suggestedAccountLineIds={suggestedAccountLineIds}
                onConfirmAccountSuggestion={onConfirmAccountSuggestion}
                suggestedItemLineIds={suggestedItemLineIds}
                onConfirmItemSuggestion={onConfirmItemSuggestion}
                accrualMode={mode}
                isAccountReviewHighlighted={activeReviewTaskId === "account"}
                isItemReviewHighlighted={
                    activeReviewTaskId === "item" ||
                    activeReviewTaskId === "suggested-item"
                }
            />
            <Field label="Memo">
                <TextArea
                    size="xl"
                    className={styles.collapsibleTextarea}
                    maxLength={2000}
                    onBlur={resetCollapsedTextAreaScroll}
                    value={state.note}
                    onChange={(event) =>
                        setState((prev) => ({
                            ...prev,
                            note: event.target.value,
                        }))
                    }
                />
            </Field>
        </VStack>
    );
};
